import React, { useCallback, useState, useRef, useEffect, useLayoutEffect } from 'react';
import { ChatHeader } from '../ChatHeader';
import { MessageList } from '../MessageList';
import { MessageInput } from '../MessageInput';
import { WelcomeScreen } from '../WelcomeScreen';
import { AssistantCard } from '../AssistantCard';
import { PolicyConsent } from '../PolicyConsent/PolicyConsent';
import { useChatTitleSubscription } from '../../hooks/useChatTitleSubscription';
import { useMessages } from '../../hooks/useMessages';
import { useMessageSubscription } from '../../hooks/useMessageSubscription';
import { useBotStreamSubscription } from '../../hooks/useBotStreamSubscription';
import { useNotifications } from '../../hooks/useNotifications';
import { useCompany } from '../../hooks/useCompany';
import { useChatStore } from '../../stores/chatStore';
import { useTheme, usePreset } from '../../lib/ThemeContext';
import { ThemeProvider } from '../../lib/ThemeContext';

/**
 * Main chat container component
 */
export const Chat: React.FC = () => {
  const themeContext = useTheme();
  const companyInfo = useCompany();
  const userFirstName = useChatStore((state) => state.userInfo.firstName);

  // Merge API data into context. Заголовок и подзаголовок из настроек компании
  // ГЛАВНЕЕ пропов вставки: текст правится в админке, а страницы клиентов с
  // захардкоженным headerText перевыкатить нельзя.
  const enrichedContext = {
    ...themeContext,
    headerText: companyInfo.headerText || themeContext.headerText,
    headerSubtitle: companyInfo.headerSubtitle || themeContext.headerSubtitle || companyInfo.name,
    telegramBotUrl: themeContext.telegramBotUrl || companyInfo.telegramBotUrl,
    privacyPolicyUrl: themeContext.privacyPolicyUrl || companyInfo.privacyPolicyUrl,
    termsOfServiceUrl: themeContext.termsOfServiceUrl || companyInfo.termsOfServiceUrl,
    greeting: pickGreeting(companyInfo.greeting, companyInfo.knownUserGreeting, userFirstName),
  };

  return (
    <ThemeProvider value={enrichedContext}>
      <ChatInner />
    </ThemeProvider>
  );
};

/**
 * The name sent with messages selects the known-user greeting; the backend saves the same choice into the history
 */
function pickGreeting(
  greeting: string | undefined,
  knownUserGreeting: string | undefined,
  userFirstName: string | undefined,
): string | undefined {
  if (userFirstName && knownUserGreeting) return knownUserGreeting.split('{userName}').join(userFirstName);
  return greeting;
}

const ChatInner: React.FC = () => {
  const { enableNotifications, onMinimize, onNewChat, greeting } = useTheme();
  const { messages: storeMessages, sendingMessage, isTyping, setTyping } = useChatStore();
  const { sendMessage, loadMore, hasMore, loading } = useMessages();
  const preset = usePreset();
  const isNv = preset === 'nv';
  const isNevi = preset === 'nevi';
  const [showAssistantCard, setShowAssistantCard] = useState(false);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const [inputHeight, setInputHeight] = useState(0);

  // Track screen transitions via useLayoutEffect — fires after DOM mutation
  // but before browser paint, so no visible flash
  const prevAssistantCard = useRef(showAssistantCard);
  const [screenAnim, setScreenAnim] = useState<string>('');

  useLayoutEffect(() => {
    let nextAnim = '';
    if (showAssistantCard && !prevAssistantCard.current) {
      nextAnim = 'wc-screen-slide-in-right';
    } else if (!showAssistantCard && prevAssistantCard.current) {
      nextAnim = 'wc-screen-slide-in-left';
    }
    prevAssistantCard.current = showAssistantCard;

    if (nextAnim) {
      setScreenAnim(nextAnim);
    }
  }, [showAssistantCard]);

  // Clear animation class after animation ends
  const handleAnimationEnd = useCallback(() => {
    setScreenAnim('');
  }, []);

  // Track input height for blur overlay alignment
  useEffect(() => {
    const el = inputWrapperRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setInputHeight(entry.contentRect.height);
      }
    });
    observer.observe(el);
    setInputHeight(el.offsetHeight);
    return () => observer.disconnect();
  }, []);

  // Subscribe to new messages
  useMessageSubscription();

  // Subscribe to progressive bot pipeline events (stages, articles, answer streaming)
  useBotStreamSubscription();

  // Subscribe to chat title updates
  useChatTitleSubscription();

  // Handle browser notifications
  useNotifications(enableNotifications);

  // Handle send message
  const handleSend = useCallback(
    async (message: string, images: string[]) => {
      try {
        // Show typing indicator
        setTyping(true);

        await sendMessage({
          message,
          images: images.length > 0 ? images : undefined,
        });

        // Typing indicator will be cleared when response arrives
      } catch (error) {
        console.error('Failed to send message:', error);
        setTyping(false);
      }
    },
    [sendMessage, setTyping],
  );

  // NV / NeVi: Show assistant card
  if ((isNv || isNevi) && showAssistantCard) {
    return (
      <div className={screenAnim} onAnimationEnd={handleAnimationEnd} style={{ height: '100%' }}>
        <AssistantCard
          onBack={() => setShowAssistantCard(false)}
          onClose={() => onMinimize?.()}
          onNewChat={onNewChat}
        />
      </div>
    );
  }

  return (
    <div
      className={`wc-relative wc-h-full ${screenAnim}`}
      onAnimationEnd={handleAnimationEnd}
      style={
        isNv
          ? {
              backgroundColor: '#F3F3F3',
              backgroundImage: [
                'radial-gradient(circle 1.5px, rgba(46, 46, 46, 0.07) 0%, transparent 100%)',
                'radial-gradient(circle 1.5px, rgba(46, 46, 46, 0.07) 0%, transparent 100%)',
              ].join(', '),
              backgroundSize: '10px 10px, 10px 10px',
              backgroundPosition: '0 0, 5px 5px',
            }
          : isNevi
            ? {
                backgroundColor: '#212121',
                backgroundImage: [
                  'radial-gradient(circle 1.5px, rgba(251, 251, 251, 0.05) 0%, transparent 100%)',
                  'radial-gradient(circle 1.5px, rgba(251, 251, 251, 0.05) 0%, transparent 100%)',
                ].join(', '),
                backgroundSize: '10px 10px, 10px 10px',
                backgroundPosition: '0 0, 5px 5px',
              }
            : { background: 'var(--wc-background-color)' }
      }
    >
      {/* Messages or Welcome screen */}
      {isNv && !greeting && storeMessages.length === 0 && !isTyping && !loading ? (
        <div className="wc-h-full wc-flex wc-flex-col">
          <WelcomeScreen />
        </div>
      ) : (
        <MessageList
          messages={storeMessages}
          isTyping={isTyping}
          isLoading={loading}
          hasMore={hasMore}
          onLoadMore={loadMore}
          bottomOffset={inputHeight}
        />
      )}

      {/* NeVi: top fade — Figma node 1905:18610 */}
      {isNevi && (
        <div
          className="wc-absolute wc-top-0 wc-left-0 wc-right-0 wc-pointer-events-none"
          style={{
            height: '110px',
            background:
              'linear-gradient(180deg, #212121 0%, rgba(33, 33, 33, 0.8) 50%, rgba(33, 33, 33, 0) 100%)',
            borderRadius: '22px 22px 0 0',
            zIndex: 2,
          }}
        />
      )}

      {/* NeVi: bottom overlay — solid fade under the input */}
      {isNevi && inputHeight > 0 && (
        <div
          className="wc-absolute wc-bottom-0 wc-left-0 wc-right-0 wc-pointer-events-none"
          style={{
            height: `${inputHeight + 24}px`,
            background: 'linear-gradient(0deg, #212121 0%, #212121 70%, rgba(33, 33, 33, 0) 100%)',
            borderRadius: '0 0 22px 22px',
            zIndex: 2,
          }}
        />
      )}

      {/* NV: blur overlay at top — gradient mask with backdrop-filter */}
      {isNv && (
        <>
          <div
            className="wc-absolute wc-top-0 wc-left-0 wc-right-0 wc-pointer-events-none"
            style={{
              height: '120px',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              maskImage: 'linear-gradient(180deg, black 0%, black 30%, transparent 100%)',
              WebkitMaskImage: 'linear-gradient(180deg, black 0%, black 30%, transparent 100%)',
              borderRadius: '30px 30px 0px 0px',
              zIndex: 2,
            }}
          />
          <div
            className="wc-absolute wc-top-0 wc-left-0 wc-right-0 wc-pointer-events-none"
            style={{
              height: '120px',
              background:
                'linear-gradient(180deg, rgba(243, 243, 243, 0.85) 0%, rgba(243, 243, 243, 0.5) 50%, rgba(243, 243, 243, 0) 100%)',
              borderRadius: '30px 30px 0px 0px',
              zIndex: 2,
            }}
          />
        </>
      )}

      {/* Header - fixed at top with glass effect */}
      <div className="wc-absolute wc-top-0 wc-left-0 wc-right-0 wc-z-10">
        <ChatHeader onCapsuleClick={isNv || isNevi ? () => setShowAssistantCard(true) : undefined} />
      </div>

      {/* NV: blur overlay at bottom — exactly covers input area, no fade above */}
      {isNv && inputHeight > 0 && (
        <>
          <div
            className="wc-absolute wc-bottom-0 wc-left-0 wc-right-0 wc-pointer-events-none"
            style={{
              height: `${inputHeight}px`,
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              borderRadius: '0 0 30px 30px',
              zIndex: 2,
            }}
          />
          <div
            className="wc-absolute wc-bottom-0 wc-left-0 wc-right-0 wc-pointer-events-none"
            style={{
              height: `${inputHeight}px`,
              background: 'rgba(243, 243, 243, 0.85)',
              borderRadius: '0 0 30px 30px',
              zIndex: 2,
            }}
          />
        </>
      )}

      {/* Input - fixed at bottom with glass effect */}
      <div ref={inputWrapperRef} className="wc-absolute wc-bottom-0 wc-left-0 wc-right-0 wc-z-10">
        {storeMessages.length === 0 && <PolicyConsent />}
        <MessageInput onSend={handleSend} disabled={sendingMessage} />
      </div>
    </div>
  );
};
